import type { AuthUser } from "@/api";

export const hasAdminPermission = (user: AuthUser | null | undefined, permission: string): boolean =>
	user?.role === "admin" || user?.effectivePermissions?.includes(permission) === true;

export const firstAllowedPath = (user: AuthUser | null | undefined): string => {
	const routes: { path: string; permission: string }[] = [
		// Dashboard leads: it is the founder workspace's home, and it is a
		// reasonable landing page for admins too (decision 1B).
		{ path: "/dashboard", permission: "page.dashboard" },
		{ path: "/finances", permission: "page.finances" },
		{ path: "/commissions", permission: "page.finances" },
		// Отчёты, заявки, лента и журнал: без них роль «только отчёты» или
		// «только журнал» получала «/login» при живой сессии, а экран входа
		// возвращал её на «/» — вечная переадресация.
		{ path: "/reports", permission: "page.reports" },
		{ path: "/applications", permission: "page.applications" },
		{ path: "/products", permission: "page.products" },
		{ path: "/catalog-import", permission: "page.catalog_import" },
		{ path: "/categories", permission: "page.categories" },
		{ path: "/feed", permission: "page.inspiration_feed" },
		{ path: "/ideas", permission: "page.ideas" },
		{ path: "/orders", permission: "page.orders" },
		{ path: "/carts", permission: "page.carts" },
		{ path: "/promos", permission: "page.promos" },
		{ path: "/ad-campaigns", permission: "page.ads" },
		{ path: "/shops", permission: "page.shops" },
		{ path: "/users", permission: "page.users" },
		{ path: "/delivery", permission: "page.delivery" },
		{ path: "/geo", permission: "page.geo" },
		{ path: "/integrations", permission: "page.integrations" },
		{ path: "/notification-campaigns", permission: "page.notification_campaigns" },
		{ path: "/support", permission: "page.support" },
		{ path: "/platform-roles", permission: "page.platform_roles" },
		{ path: "/audit", permission: "action.audit.view" },
	];
	return routes.find((route) => hasAdminPermission(user, route.permission))?.path ?? "/login";
};

/**
 * Пункты меню консоли с отметкой «закрыто» (09.10.2026).
 *
 * Обычно закрытые разделы в меню не показываются вовсе, а группа без единого
 * открытого раздела пропадает. У роли с showLockedNav (например, «Маркетолог»)
 * закрытые разделы остаются на месте с замком: сотрудник видит, что раздел
 * есть, но он не в его роли. Полному администратору замки не нужны.
 */
export const navWithLocks = <G extends { items: { permission: string }[] }>(
	groups: G[],
	user: AuthUser | null | undefined,
): (Omit<G, "items"> & { items: (G["items"][number] & { locked: boolean })[] })[] => {
	const showLocked = user?.role !== "admin" && user?.platformRole?.showLockedNav === true;
	return groups
		.map((group) => ({
			...group,
			items: group.items
				.map((entry) => ({ ...entry, locked: !hasAdminPermission(user, entry.permission) }))
				.filter((entry) => showLocked || !entry.locked),
		}))
		.filter((group) => group.items.length > 0);
};

